// HomeView.tsx
import React, { useState } from 'react';
import Layout from '../../layout/Layout';
import DigestStackedView from '../../digest/DigestStackedView';
import styles from './HomeView.module.css';
import DigestListView from '../../digest/DigestListView';
import { DigestViewProvider } from '../../digest/DigestViewContext';
import { motion } from "framer-motion";
import DataSetupComponent from '../../setup/DataSetupComponent';
import Spinner from '../../button/Spinner';
import { useDigestData } from '../data/useDigestData';
import { User } from '../../../gql/types';
import { HomeFooterProvider } from '../context/HomeFooterContext';
import { HomeFooter } from './HomeFooter';
import { getNextSurface } from './getNextSurface';
import ConnectionsHubView from './connections/ConnectionsHubView';
import { useConnectionHubPagesData } from '../data/useConnectionHubPagesData';

interface HomeViewProps {
    user: User;
}

export enum HomeSurface {
    DIGEST,
    DIGEST_LIST,
    CONNECTIONS
}

const HomeView: React.FC<HomeViewProps> = ({ user }) => {
    const [currentSurface, setCurrentSurface] = useState<HomeSurface>(HomeSurface.DIGEST);
    const [isDigestVisible, setIsDigestVisible] = useState(true);
    const [prevButtonEnabled, setPrevButtonEnabled] = useState(false);
    const [triggerPrevSignal, setTriggerPrevSignal] = useState(0);

    const {
        currentDigest,
        loading,
        showDataLoadingScreen,
        dataLoadingCallback,
    } = useDigestData();

    const {
        pages,
        loading: pagesLoading,
    } = useConnectionHubPagesData();

    const handleSwitchView = () => {
        setCurrentSurface((prev) => {
            return getNextSurface(prev);
        });
    };

    const handlePrevCard = () => {
        setTriggerPrevSignal((prev) => prev < 100 ? prev + 1 : 1);
    };

    const loadingPage = (
        <div className={styles.homeLoadingScreen}>
            <div className={styles.spinnerContainer}>
                <Spinner size={36} className={styles.spinner} />
            </div>
        </div>
    );

    const renderSurface = () => {
        let content;
        switch (currentSurface) {
            case HomeSurface.DIGEST_LIST:
                if (loading) {
                    return loadingPage;
                }
                content = (<DigestListView digest={currentDigest} />);
                break;
            case HomeSurface.DIGEST:
                if (loading) {
                    return loadingPage;
                }
                content = (
                    <DigestStackedView
                        setEnablePrevButton={setPrevButtonEnabled}
                        digest={currentDigest}
                        triggerPrevSignal={triggerPrevSignal}
                    />
                );
                break;
            case HomeSurface.CONNECTIONS:
                if (pagesLoading) {
                    return loadingPage;
                }
                content = (
                    <ConnectionsHubView
                        setEnablePrevButton={setPrevButtonEnabled}
                        pages={pages}
                        triggerPrevSignal={triggerPrevSignal}
                        isSelectMode={false}
                    />
                )
                break;
            default:
                return null; // or fallback component
        }

        return (
            <motion.div
                className={styles.homeContainer}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
            >
                {content}
            </motion.div>);
    };

    return (
        <HomeFooterProvider>
            <DigestViewProvider setIsDigestVisible={setIsDigestVisible}>
                <Layout
                    navbarConfig={{ title: "", showProfileBubble: true, showLogo: true, notificationsView: true, inboxView: true }}
                    isHidden={!isDigestVisible}
                    footer={(
                        <HomeFooter
                            enablePrevButton={prevButtonEnabled}
                            onPrevCard={handlePrevCard}
                            onSwitchView={handleSwitchView}
                            currentSurface={currentSurface}
                            loading={showDataLoadingScreen}
                        />
                    )}
                    containerConfig={
                        {
                            noPadding: true,
                        }
                    }
                    user={user}
                >
                    {showDataLoadingScreen
                        ? <DataSetupComponent
                            user={user}
                            onComplete={dataLoadingCallback}
                            displayLogo={false}
                            className={styles.loadingScreen}
                            initialMessage={"Checking your schedule..."} />
                        : (loading && pagesLoading
                            ? <div className={styles.homeLoadingScreen}>
                                <div className={styles.spinnerContainer}>
                                    <Spinner size={36} className={styles.spinner} />
                                </div>
                            </div>
                            : renderSurface())}
                </Layout>
            </DigestViewProvider>
        </HomeFooterProvider>
    );
};

export default HomeView;
